import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Funnel,
  FunnelChart,
  LabelList,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { PageHeader } from '@/components/layout/PageHeader';
import { Alert, Button, Card, Spinner } from '@/components/ui';
import { customReportsApi } from '@/services/customReports.service';
import { getErrorMessage } from '@/services/apiClient';
import type { ReportResult, ReportResultType } from '@/types/customReports.types';
import { orderIdOf, parseFilterText, resultIdOf } from './resultHelpers';

/**
 * Chart-type enum.
 *   1-5 mirror the legacy `os` values from
 *   report_visual_display3.php lines 294-329 (Table / Line / Area / Bar /
 *   Column).
 *   6  reactivates the legacy ComboChart slot (bars stacked with a
 *      totals-trend line overlay).
 *   7+ are v5 additions chosen for medspa workflows that legacy never had.
 */
type ChartType = '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10';

/**
 * The legacy `subFiltersText` column is a `;`-delimited list of filter
 * labels (admin_report_list.php strips `;` from each label, so the names
 * never contain a semicolon). Individual labels MAY contain other
 * characters such as `|` (e.g. the facility "Next|Health Century City"),
 * which we deliberately keep intact — we only split on `;`.
 */
function formatSubFiltersText(raw: string | null | undefined): string {
  if (!raw) return '—';
  if (raw === 'Combined Total') return raw;
  const parts = raw
    .split(';')
    .map((s) => s.trim())
    .filter((s) => s !== '');
  return parts.length > 0 ? parts.join(', ') : '—';
}

const CHART_TYPE_OPTIONS: Array<{ value: ChartType; label: string }> = [
  { value: '1',  label: 'Table' },
  { value: '2',  label: 'Line Chart' },
  { value: '3',  label: 'Area Chart' },
  { value: '4',  label: 'Bar Chart' },
  { value: '5',  label: 'Column Chart' },
  { value: '6',  label: 'Combo Chart' },
  { value: '9',  label: '100% Stacked Bar' },
  { value: '10', label: '100% Stacked Column' },
  { value: '7',  label: 'Funnel' },
  { value: '8',  label: 'Donut' },
];

/**
 * Renders a persisted run row, replacing legacy `report_visual_display3.php`.
 *
 *   • Parses `reportData`/`colorData` (`[r1c1,r1c2,...],...`) into a 2-D matrix.
 *   • `labelData`/`labelData2`/`totalData` = comma-separated buckets/series/totals.
 *   • Renders a Recharts stacked-bar chart + data table.
 *   • Download XLSX is rendered ONLY when `fileName` is set, mirroring
 *     legacy `if ($fileName != "" && $min == false)` on
 *     report_visual_display3.php line 528.
 *   • Run-history `Generated:` dropdown is rendered ONLY for CST results
 *     (legacy `isset($_GET['resultsID'])` branch, lines 601-617). CDM
 *     just shows a static date label.
 *   • Re-deliver intentionally NOT exposed here — legacy has no
 *     re-deliver on this surface (audit section F).
 */
export default function ReportResultViewer() {
  const { resultId } = useParams<{ resultId: string }>();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const type: ReportResultType = (params.get('type') as ReportResultType) === 'cst' ? 'cst' : 'cdm';
  const id = Number(resultId);

  const [actionError, setActionError] = useState<string | null>(null);

  const result = useQuery<ReportResult>({
    queryKey: ['reports', 'results', type, id],
    queryFn: () => customReportsApi.results.get(id, type),
    enabled: Number.isFinite(id) && id > 0,
  });

  // Run-history dropdown source — CST only. Legacy CDM has no picker.
  const orderId = result.data ? orderIdOf(result.data) : 0;
  const siblings = useQuery<ReportResult[]>({
    queryKey: ['reports', 'results', type, 'order', orderId],
    queryFn: () => customReportsApi.results.list({ type, orderId }),
    enabled: type === 'cst' && Boolean(orderId),
    staleTime: 60_000,
  });

  const download = async () => {
    if (!result.data?.fileName) return;
    setActionError(null);
    try {
      const file = await customReportsApi.results.download(id, { type });
      const a = document.createElement('a');
      // A signed S3 link names the file in its own response header; the
      // download attribute only applies to the same-origin blob URL.
      a.href = 'url' in file ? file.url : URL.createObjectURL(file.blob);
      a.download = result.data.fileName.split('/').pop() ?? `report-${id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      if ('blob' in file) URL.revokeObjectURL(a.href);
    } catch (err) {
      setActionError(
        getErrorMessage(
          err,
          "The spreadsheet couldn't be downloaded. Try again, and if it keeps happening, contact Prospera support.",
        ),
      );
    }
  };

  if (result.isLoading) {
    return (
      <div className="p-6">
        <PageHeader variant="compact" title="Report Result" />
        <div className="flex items-center gap-2 text-slate-500">
          <Spinner size="sm" /> Loading result…
        </div>
      </div>
    );
  }

  if (result.isError || !result.data) {
    return (
      <div className="p-6">
        <PageHeader variant="compact" title="Report Result" />
        <Alert variant="error" title="Could not load result">
          {result.isError ? getErrorMessage(result.error) : 'Result not found.'}
        </Alert>
      </div>
    );
  }

  return (
    <ReportResultBody
      row={result.data}
      type={type}
      siblings={siblings.data ?? []}
      currentId={id}
      onPickRun={(siblingId) =>
        navigate(`/reports/results/${siblingId}?type=${type}`)
      }
      onBack={() => navigate('/reports/my-reports')}
      onDownload={download}
      actionError={actionError}
    />
  );
}

// ─── Body (with parsed matrix + chart + table) ──────────────────────────────

function ReportResultBody({
  row,
  type,
  siblings,
  currentId,
  onPickRun,
  onBack,
  onDownload,
  actionError,
}: {
  row: ReportResult;
  type: ReportResultType;
  siblings: ReportResult[];
  currentId: number;
  onPickRun: (siblingId: number) => void;
  onBack: () => void;
  onDownload: () => void;
  actionError: string | null;
}) {
  const parsed = useMemo(() => parseResultMatrix(row), [row]);
  const hasFile = Boolean(row.fileName);
  const title = row.title ?? row.reportTitle ?? `Result #${row.reportsOndemandResultsID ?? row.reportsCustomResultsID}`;
  const filterSegments = useMemo(() => parseFilterText(row.filterText), [row.filterText]);

  // Chart type defaults to Table (`os=1`) per legacy
  // report_visual_display3.php line 294 (`$outputStyle = "1"`).
  const [chartType, setChartType] = useState<ChartType>('1');

  // Siblings list, newest first. CST-only — legacy CDM shows a static
  // `Generated: {date}` label instead (report_visual_display3.php
  // lines 620-621), so we render that fallback below.
  const runOptions = useMemo(
    () =>
      [...siblings]
        .map((s) => ({ id: resultIdOf(s), dateCreated: s.dateCreated ?? '' }))
        .filter((s) => s.id > 0)
        .sort((a, b) => {
          const at = a.dateCreated ? new Date(a.dateCreated).getTime() : 0;
          const bt = b.dateCreated ? new Date(b.dateCreated).getTime() : 0;
          return bt - at;
        }),
    [siblings],
  );
  const showRunPicker = type === 'cst' && runOptions.length > 1;
  const staticGenerated = !showRunPicker ? formatGeneratedLegacy(row.dateCreated) : '';

  return (
    <div className="p-6">
      <PageHeader
        variant="compact"
        title={title}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {showRunPicker ? (
              <label className="flex items-center gap-2 text-xs font-medium text-slate-600">
                Generated:
                <select
                  className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-800 focus:border-slate-900 focus:outline-none"
                  value={String(currentId)}
                  onChange={(e) => onPickRun(Number(e.target.value))}
                >
                  {runOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.dateCreated
                        ? formatGeneratedLegacy(opt.dateCreated)
                        : `Run #${opt.id}`}
                    </option>
                  ))}
                </select>
              </label>
            ) : staticGenerated ? (
              <span className="text-xs text-slate-600">
                Generated: <span className="font-medium text-slate-800">{staticGenerated}</span>
              </span>
            ) : null}
            {/* Chart-type selector — mirrors the os=1..5 <select> in
                report_visual_display3.php lines 493-527. Default Table per
                legacy line 294. Hidden when there is no chart data to
                render. */}
            {parsed.data.length > 0 && (
              <label className="flex items-center gap-2 text-xs font-medium text-slate-600">
                Type:
                <select
                  className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-800 focus:border-slate-900 focus:outline-none"
                  value={chartType}
                  onChange={(e) => setChartType(e.target.value as ChartType)}
                >
                  {CHART_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <Button variant="primary" size="sm" onClick={onBack}>
              Back to Reports
            </Button>
            {hasFile && (
              <Button
                variant="primary"
                size="sm"
                onClick={onDownload}
                title="Download stored XLSX"
              >
                Download XLSX
              </Button>
            )}
          </div>
        }
      />

      {actionError && (
        <div className="mb-4">
          <Alert variant="error">{actionError}</Alert>
        </div>
      )}

      {parsed.data.length === 0 ? (
        <Card>
          <div className="py-16 text-center text-sm text-slate-500">
            This result row has no chart data (reportData column is empty).
          </div>
        </Card>
      ) : (
        <>
          {/* os=1 (Table) hides the chart entirely — legacy renders only
              the Google Visualization Table in that mode. */}
          {chartType !== '1' && (
            <Card className="mb-4">
              <ResultChart parsed={parsed} chartType={chartType} />
            </Card>
          )}
          <Card className="mb-4">
            <ResultTable parsed={parsed} />
          </Card>
        </>
      )}

      <Card shadow="none" className="bg-slate-50">
        <h3 className="mb-2 text-sm font-semibold text-slate-700">Details</h3>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-xs sm:grid-cols-2 lg:grid-cols-3">
          <MetaRow label="Report title" value={row.reportTitle ?? '—'} />
          <MetaRow label="Report subtitle" value={row.reportSubTitle ?? '—'} />
          <MetaRow label="Options title" value={row.reportOptionsTitle ?? '—'} />
          <MetaRow label="Sub-filters" value={formatSubFiltersText(row.subFiltersText)} />
          <MetaRow label="Active" value={String(row.active ?? '')} />
        </dl>

        {filterSegments.length > 0 && (
          <div className="mt-4 border-t border-slate-200 pt-3">
            <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Filters
            </h4>
            <div className="space-y-1 text-xs text-slate-700">
              {filterSegments.map((seg) => (
                <div key={seg.typeId}>
                  <span className="font-semibold text-slate-800">
                    {seg.header}
                    {seg.overall ? '*' : ''}:
                  </span>{' '}
                  {seg.labels.join(', ')}
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="font-medium text-slate-500">{label}</dt>
      <dd className="sm:col-span-1 lg:col-span-2 truncate text-slate-800">{value}</dd>
    </>
  );
}

/**
 * Legacy `date("Y-m-d g:i A", strtotime(...))` from
 * report_visual_display3.php lines 615-621. Returns an empty string for
 * missing/invalid input so callers can fall back to a placeholder.
 */
function formatGeneratedLegacy(raw: string | null | undefined): string {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  const date = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return `${date} ${time}`;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

// ─── Chart ───────────────────────────────────────────────────────────────────

const DEFAULT_COLORS = [
  '#0f766e',
  '#be185d',
  '#1d4ed8',
  '#ca8a04',
  '#7c3aed',
  '#dc2626',
  '#059669',
  '#ea580c',
];

/**
 * Switches between the chart types selectable in the viewer.
 *   1-5 mirror legacy `os` (Table / Line / Area / Bar / Column).
 *   6   Combo  — stacked columns with a total-trend line overlay (revives
 *                legacy `os=6` slot which was defined but never wired).
 *   7   Funnel — one stage per series, value = row total (or single-bucket
 *                value when there is only one column). Useful for the
 *                conversion-pipeline report families.
 *   8   Donut  — Pie chart with a hollow center; one slice per series,
 *                same value mapping as Funnel. Useful for share-of-total
 *                views like lead source / payment type mix.
 *   9   100% Stacked Bar    — bars with each column normalized to 100%.
 *   10  100% Stacked Column — columns with each X-bucket normalized to 100%.
 */
function ResultChart({
  parsed,
  chartType,
}: {
  parsed: ParsedResult;
  chartType: ChartType;
}) {
  const chartData = useMemo(() => {
    return parsed.labels.map((label, colIdx) => {
      const point: Record<string, number | string> = { label };
      parsed.rowNames.forEach((name, rowIdx) => {
        point[name] = parsed.data[rowIdx]?.[colIdx] ?? 0;
      });
      return point;
    });
  }, [parsed]);

  // Pick a stable series color: prefer the per-cell hex stored in
  // `colorData`, fall back to a palette indexed by row.
  const seriesColor = (rowIdx: number, colIdx: number): string => {
    const stored = parsed.colors[rowIdx]?.[colIdx];
    if (stored) return normalizeHex(stored);
    return DEFAULT_COLORS[rowIdx % DEFAULT_COLORS.length];
  };
  const lineColor = (rowIdx: number): string =>
    seriesColor(rowIdx, 0);

  // Sum-by-row used by Funnel + Donut. Falls back to the precomputed
  // `totalData` array when present (most legacy report families populate
  // it), otherwise sums the row's cells.
  const rowTotal = (rowIdx: number): number => {
    const stored = parsed.totals[rowIdx];
    if (Number.isFinite(stored) && stored !== 0) return stored;
    const row = parsed.data[rowIdx] ?? [];
    return row.reduce((sum, v) => sum + (Number(v) || 0), 0);
  };

  if (chartType === '2') {
    // Line Chart — one <Line> per series, no stacking, distinct hex.
    return (
      <div style={{ width: '100%', height: 360 }}>
        <ResponsiveContainer>
          <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 16 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#475569' }} />
            <YAxis tick={{ fontSize: 11, fill: '#475569' }} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {parsed.rowNames.map((name, rowIdx) => (
              <Line
                key={name}
                type="monotone"
                dataKey={name}
                stroke={lineColor(rowIdx)}
                strokeWidth={2}
                dot={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (chartType === '3') {
    // Area Chart — stacked. Legacy Google AreaChart defaults to stacked
    // when multiple series share an X.
    return (
      <div style={{ width: '100%', height: 360 }}>
        <ResponsiveContainer>
          <AreaChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 16 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#475569' }} />
            <YAxis tick={{ fontSize: 11, fill: '#475569' }} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {parsed.rowNames.map((name, rowIdx) => (
              <Area
                key={name}
                type="monotone"
                dataKey={name}
                stackId="series"
                stroke={lineColor(rowIdx)}
                fill={lineColor(rowIdx)}
                fillOpacity={0.4}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (chartType === '6') {
    // Combo Chart — stacked columns with a "Total" line overlaid on a
    // secondary Y-axis. Reactivates the legacy os=6 slot that PHP defined
    // but never wired into the <select>. Useful for showing per-series
    // composition AND the total trend over the same time window.
    const comboData = chartData.map((point, colIdx) => {
      const total = parsed.rowNames.reduce(
        (sum, name) => sum + (Number(point[name]) || 0),
        0,
      );
      void colIdx;
      return { ...point, __total: total };
    });
    return (
      <div style={{ width: '100%', height: 360 }}>
        <ResponsiveContainer>
          <ComposedChart data={comboData} margin={{ top: 8, right: 24, left: 0, bottom: 16 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#475569' }} />
            <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#475569' }} />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 11, fill: '#475569' }}
            />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {parsed.rowNames.map((name, rowIdx) => (
              <Bar key={name} yAxisId="left" dataKey={name} stackId="series">
                {parsed.labels.map((_, colIdx) => (
                  <Cell
                    key={`cell-${rowIdx}-${colIdx}`}
                    fill={seriesColor(rowIdx, colIdx)}
                  />
                ))}
              </Bar>
            ))}
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="__total"
              name="Total"
              stroke="#0f172a"
              strokeWidth={2}
              dot={{ r: 3 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (chartType === '7') {
    // Funnel — one stage per series row, value = row total (preferring
    // the precomputed `totalData` when present). Sorted in the row order
    // the user picked, since families like AppointmentConversions
    // pre-order stages.
    const funnelData = parsed.rowNames
      .map((name, rowIdx) => ({
        name: name || `Stage ${rowIdx + 1}`,
        value: rowTotal(rowIdx),
        fill: lineColor(rowIdx),
      }))
      .filter((s) => s.value > 0);

    if (funnelData.length === 0) {
      return <EmptyChartNotice />;
    }
    return (
      <div style={{ width: '100%', height: 360 }}>
        <ResponsiveContainer>
          <FunnelChart margin={{ top: 8, right: 16, left: 16, bottom: 16 }}>
            <Tooltip />
            <Funnel dataKey="value" data={funnelData} isAnimationActive>
              <LabelList
                position="right"
                fill="#475569"
                stroke="none"
                fontSize={11}
                dataKey="name"
              />
              <LabelList
                position="center"
                fill="#fff"
                stroke="none"
                fontSize={12}
                fontWeight={600}
                dataKey="value"
              />
            </Funnel>
          </FunnelChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (chartType === '8') {
    // Donut — one slice per series row, value = row total. Inner radius
    // gives the modern donut look; switch to 0 for a classic pie.
    const pieData = parsed.rowNames
      .map((name, rowIdx) => ({
        name: name || `Series ${rowIdx + 1}`,
        value: rowTotal(rowIdx),
        fill: lineColor(rowIdx),
      }))
      .filter((s) => s.value > 0);

    if (pieData.length === 0) {
      return <EmptyChartNotice />;
    }
    return (
      <div style={{ width: '100%', height: 360 }}>
        <ResponsiveContainer>
          <PieChart>
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius="55%"
              outerRadius="80%"
              stroke="#fff"
              strokeWidth={2}
              paddingAngle={1}
              label={(entry) =>
                `${formatPercent(entry.value, pieData.reduce((s, p) => s + p.value, 0))}`
              }
            >
              {pieData.map((slice, idx) => (
                <Cell key={slice.name} fill={slice.fill ?? lineColor(idx)} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (chartType === '9' || chartType === '10') {
    // 100% Stacked Bar (9) / 100% Stacked Column (10). Recharts has no
    // built-in percent mode so we pre-normalize each X-bucket so series
    // sum to 100. We project each series value as its share of the total
    // for that bucket; tooltip preserves the original value via a hidden
    // shadow key.
    const normalized = chartData.map((point) => {
      const total = parsed.rowNames.reduce(
        (sum, name) => sum + (Number(point[name]) || 0),
        0,
      );
      const out: Record<string, number | string> = { label: point.label };
      parsed.rowNames.forEach((name) => {
        const raw = Number(point[name]) || 0;
        out[name] = total > 0 ? (raw / total) * 100 : 0;
        out[`${name}__raw`] = raw;
      });
      return out;
    });
    const horizontal = chartType === '9';
    return (
      <div style={{ width: '100%', height: 360 }}>
        <ResponsiveContainer>
          <BarChart
            data={normalized}
            layout={horizontal ? 'vertical' : 'horizontal'}
            margin={{ top: 8, right: 16, left: 0, bottom: 16 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            {horizontal ? (
              <>
                <XAxis
                  type="number"
                  domain={[0, 100]}
                  tickFormatter={(v) => `${Math.round(Number(v))}%`}
                  tick={{ fontSize: 11, fill: '#475569' }}
                />
                <YAxis
                  type="category"
                  dataKey="label"
                  tick={{ fontSize: 11, fill: '#475569' }}
                />
              </>
            ) : (
              <>
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#475569' }} />
                <YAxis
                  domain={[0, 100]}
                  tickFormatter={(v) => `${Math.round(Number(v))}%`}
                  tick={{ fontSize: 11, fill: '#475569' }}
                />
              </>
            )}
            <Tooltip formatter={(v: number) => `${Number(v).toFixed(1)}%`} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {parsed.rowNames.map((name, rowIdx) => (
              <Bar key={name} dataKey={name} stackId="series">
                {parsed.labels.map((_, colIdx) => (
                  <Cell
                    key={`cell-${rowIdx}-${colIdx}`}
                    fill={seriesColor(rowIdx, colIdx)}
                  />
                ))}
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // chartType 4 = Bar (horizontal) and chartType 5 = Column (vertical) both
  // use Recharts <BarChart>; only the layout flips. We still stack series so
  // the legacy "stacked totals" semantics match.
  const horizontalBars = chartType === '4';
  return (
    <div style={{ width: '100%', height: 360 }}>
      <ResponsiveContainer>
        <BarChart
          data={chartData}
          layout={horizontalBars ? 'vertical' : 'horizontal'}
          margin={{ top: 8, right: 16, left: 0, bottom: 16 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          {horizontalBars ? (
            <>
              <XAxis type="number" tick={{ fontSize: 11, fill: '#475569' }} />
              <YAxis
                type="category"
                dataKey="label"
                tick={{ fontSize: 11, fill: '#475569' }}
              />
            </>
          ) : (
            <>
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#475569' }} />
              <YAxis tick={{ fontSize: 11, fill: '#475569' }} />
            </>
          )}
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {parsed.rowNames.map((name, rowIdx) => (
            <Bar key={name} dataKey={name} stackId="series">
              {parsed.labels.map((_, colIdx) => (
                <Cell
                  key={`cell-${rowIdx}-${colIdx}`}
                  fill={seriesColor(rowIdx, colIdx)}
                />
              ))}
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function EmptyChartNotice() {
  return (
    <div className="py-16 text-center text-sm text-slate-500">
      Not enough data to render this chart type.
    </div>
  );
}

function formatPercent(value: number, total: number): string {
  if (!total || total === 0) return '0%';
  return `${((value / total) * 100).toFixed(1)}%`;
}

// ─── Table ───────────────────────────────────────────────────────────────────

function ResultTable({ parsed }: { parsed: ParsedResult }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50">
            <th className="px-3 py-2 text-left font-medium text-slate-600">Series</th>
            {parsed.labels.map((l) => (
              <th key={l} className="px-3 py-2 text-right font-medium text-slate-600">
                {l}
              </th>
            ))}
            <th className="px-3 py-2 text-right font-semibold text-slate-700">Total</th>
          </tr>
        </thead>
        <tbody>
          {parsed.rowNames.map((name, rowIdx) => {
            const row = parsed.data[rowIdx] ?? [];
            const total = parsed.totals[rowIdx] ?? row.reduce((sum, v) => sum + (Number(v) || 0), 0);
            return (
              <tr key={name} className="border-b border-slate-100">
                <td className="px-3 py-2 font-medium text-slate-800">{name}</td>
                {parsed.labels.map((_, colIdx) => (
                  <td key={colIdx} className="px-3 py-2 text-right tabular-nums text-slate-700">
                    {formatCell(row[colIdx])}
                  </td>
                ))}
                <td className="px-3 py-2 text-right font-semibold tabular-nums text-slate-900">
                  {formatCell(total)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function formatCell(v: number | string | undefined): string {
  if (v == null) return '';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  // Preserve fractional values (percent / median reports) while keeping whole
  // numbers un-decorated. Limits runaway precision from float noise.
  return Number.isInteger(n) ? n.toLocaleString() : n.toFixed(2);
}

function normalizeHex(raw: string): string {
  const s = raw.replace(/^["']|["']$/g, '').trim();
  if (!s) return '#64748b';
  return s.startsWith('#') ? s : `#${s}`;
}

// ─── Parser: legacy comma/bracket wire format ────────────────────────────────

interface ParsedResult {
  labels: string[];
  rowNames: string[];
  data: number[][];
  colors: string[][];
  totals: number[];
}

/**
 * Legacy storage format (see autorun_pbi.php:1662):
 *
 *   reportData      = "[r1c1,r1c2,...],[r2c1,r2c2,...],...[]"
 *   colorData       = '["#hex1"],["#hex2"],...'
 *   labelData       = "Jan,Feb,Mar,..."  (X-axis buckets, comma-separated, may be quoted)
 *   subFiltersText  = "Series A;Series B;..."  (row/series names — SEMICOLON-delimited, URL-encoded)
 *   totalData       = "123,456,..."      (per-row totals)
 *
 * Important: legacy `report_visual_display3.php` reads chart row/series
 * names from `subFiltersText` (split on `;`, lines 281 & 432-438), NOT
 * from `labelData2`. labelData2 is just a duplicate of labelData in most
 * families (see report_generator_autorun_pbi.php line 1637). We follow
 * legacy and source the series labels from `subFiltersText`.
 *
 * Legacy never synthesizes "Series N" / "Bucket N" fallbacks — if the
 * data is malformed the chart simply skips that series/column. We match
 * that behaviour.
 */
function parseResultMatrix(row: ReportResult): ParsedResult {
  const labels = splitCsvQuoted(row.labelData);
  const rowNames = splitSemicolonDecoded(row.subFiltersText);
  const data = parseBracketMatrix(row.reportData, 'number') as number[][];
  const colors = parseBracketMatrix(row.colorData, 'string') as string[][];
  const totals = splitCsvQuoted(row.totalData).map((v) => Number(v) || 0);

  // If subFiltersText is short and data has more rows, expose those extra
  // rows in the data table with a blank name (legacy renders blank cells
  // rather than a synthesized "Series N").
  while (rowNames.length < data.length) {
    rowNames.push('');
  }

  return { labels, rowNames, data, colors, totals };
}

/**
 * Split `"a","b","c"` or `a,b,c` (legacy stores labelData both ways —
 * quoted in some families, raw in others). Drops empties.
 */
function splitCsvQuoted(raw: string | null | undefined): string[] {
  if (!raw) return [];
  return String(raw)
    .split(',')
    .map((s) => s.trim().replace(/^["']|["']$/g, ''))
    .filter((s) => s.length > 0);
}

/**
 * Split `subFiltersText` on `;` and URL-decode each entry. Mirrors
 * `explode(";", urldecode(...))` from report_visual_display3.php line 281.
 */
function splitSemicolonDecoded(raw: string | null | undefined): string[] {
  if (!raw) return [];
  let decoded = String(raw);
  try {
    decoded = decodeURIComponent(decoded.replace(/\+/g, ' '));
  } catch {
    // Stored value may already be plain text — fall through unmodified.
  }
  return decoded
    .split(';')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function parseBracketMatrix(
  raw: string | null | undefined,
  kind: 'number' | 'string',
): Array<Array<number | string>> {
  if (!raw) return [];
  const out: Array<Array<number | string>> = [];
  // Match every `[ ... ]` group; inner content is a comma-separated row.
  const matches = String(raw).match(/\[([^\]]*)\]/g);
  if (!matches) return [];
  for (const group of matches) {
    const inner = group.slice(1, -1);
    if (inner === '') {
      out.push([]);
      continue;
    }
    const parts = inner.split(',').map((s) => s.trim().replace(/^["']|["']$/g, ''));
    if (kind === 'number') {
      out.push(parts.map((p) => (p === '' ? 0 : Number(p) || 0)));
    } else {
      out.push(parts);
    }
  }
  return out;
}
